import React, { FC } from 'react';
import styled from 'styled-components';
import { Field, FieldProps } from 'formik';
import { RadioCheckboxLabel, RadioCheckboxSizeValue, getRadioCheckOuterSize, getRadioInnerSize } from './utils';

/** Styled Components */
type RadioProps = {
  $size: RadioCheckboxSizeValue;
  disabled?: boolean;
};

const StyledRadio = styled.input<RadioProps>`
  appearance: none;
  background-color: ${(props): string => props.theme.designSystem.bgColors.card};
  margin: 0;
  font: inherit;
  border: 1px solid
    ${({ theme, disabled }): string =>
      disabled ? theme.designSystem.borderColors.default : theme.designSystem.borderColors.divider};
  border-radius: 50%;
  height: ${(props): string => getRadioCheckOuterSize(props.$size)};
  width: ${(props): string => getRadioCheckOuterSize(props.$size)};
  display: grid;
  place-content: center;
  cursor: ${(props): string => (props.disabled ? 'default' : 'pointer')};

  &::before {
    content: '';
    height: ${(props): string => getRadioInnerSize(props.$size)};
    width: ${(props): string => getRadioInnerSize(props.$size)};
    background-color: ${(props): string => props.theme.designSystem.bgColors.card};
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
  }

  &:checked {
    background-color: ${({ theme, disabled }): string =>
      disabled ? theme.designSystem.bgColors.disabled : theme.designSystem.bgColors.pillSelected};
  }

  &:checked::before {
    transform: scale(1);
  }

  &:focus {
    outline: max(2px, 0.15em) solid
      ${({ theme, disabled }): string =>
        disabled ? theme.designSystem.borderColors.default : theme.designSystem.borderColors.divider};
    outline-offset: max(2px, 0.15em);
  }
`;

/** Types */
type HtmlInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur' | 'value' | 'multiple' | 'type' | 'id'
>;

export type RadioInputProps = Omit<HtmlInputProps, 'size'> & {
  /**
   * callback invoked with the value of the selected radio
   */
  onChange?: (newValue: string) => void;
  /**
   * value of this radio option
   */
  value: string;
  /**
   * is this option currently selected
   */
  checked: boolean;
  testId?: string;
  qaId?: string;
  /**
   * label to display next to the radio
   */
  label?: string;
  /**
   * whether to render a small or large radio (default: "sm")
   */
  size?: RadioCheckboxSizeValue;
  id?: string;
};

/**
 * A simple, controlled radio button input with label
 * @example
 * const [selectedValue, setSelectedValue] = useState<string>('');
 * <RadioInput
      value="myValue"
      onChange={() => setSelectedValue('myValue')}
      checked={selectedValue === 'myValue'}
      label="My Value"
    />
 */
export const RadioInput = ({
  className,
  onChange,
  testId,
  qaId,
  label,
  size = 'sm' as RadioCheckboxSizeValue,
  disabled,
  id,
  value,
  checked,
  ...inputProps
}: RadioInputProps) => {
  return (
    <RadioCheckboxLabel className={className} $size={size} disabled={disabled}>
      <StyledRadio
        {...inputProps}
        $size={size}
        data-testid={testId}
        data-qa-id={qaId}
        onChange={(e) => onChange(e.target.value)}
        type="radio"
        id={id}
        value={value}
        checked={checked}
        disabled={disabled}
      />
      {label}
    </RadioCheckboxLabel>
  );
};

export interface ConnectedRadioInputProps {
  inputProps: Omit<RadioInputProps, 'onChange' | 'onBlur' | 'name' | 'isValid' | 'isInvalid' | 'checked'>;
}

/**
 * A radio button input with label that is connected to Formik state
 * In general this should not be used directly because we should have multiple radio options and use the RadioGroupInput component

 * @example
 * <ConnectedRadioInput
      name="fieldname"
      inputProps={{ value: 'myValue', label: 'My Value' }}
    />
 */
export const ConnectedRadioInput: FC<ConnectedRadioInputProps & { name: string }> = ({ name, inputProps }) => (
  <Field name={name}>
    {({ field: { value: formikValue }, form: { errors, setFieldValue } }: FieldProps<string>) => {
      const hasError = errors?.[name];
      const fieldValidation = hasError ? { invalid: true } : {};

      const onChange = (newVal: string) => {
        setFieldValue(name, newVal);
      };

      // this prevents the console warning about switching between
      // controlled and uncontrolled input types
      let inputValue = formikValue;

      if (inputValue === undefined) {
        inputValue = null;
      }

      return (
        <RadioInput
          {...inputProps}
          {...fieldValidation}
          value={inputProps.value}
          name={name}
          onChange={onChange}
          checked={inputValue === inputProps.value}
        />
      );
    }}
  </Field>
);
